import {
    STORE_INFO_CONTACT_FIELDS,
    STORE_INFO_CONTACT_ROLES,
} from "@/config/sellerProfile/storeInfo";

function emptyContact() {
    return {
        name: "",
        email: "",
        phone: "",
    };
}

export function emptyCompanyStoreDetails() {
    return {
        legalCompanyName: "",
        registeredAddress: "",
        city: "",
        countryCode: "",
        countryLabel: "",
        vatNumber: "",
        companyRegistrationNumber: "",
        webshopUrl: "",
        webshopName: "",
        logoUrl: "",
        description: "",
    };
}

export function emptyKeyContacts() {
    return Object.fromEntries(
        STORE_INFO_CONTACT_ROLES.map((role) => [role.payloadKey, emptyContact()])
    );
}

export function emptyCustomerSupport() {
    return {
        supportEmail: "",
        supportPhone: "",
        faqUrl: "",
        generalTermsUrl: "",
        returnPolicy: "",
    };
}

export function emptyBenefit() {
    return {
        type: "",
        name: "",
        description: "",
    };
}

export function emptyPerksLoyaltyBenefits() {
    return {
        benefits: [],
    };
}

export function sanitizeBenefits(benefits) {
    return (Array.isArray(benefits) ? benefits : [])
        .map((benefit) => ({
            type: String(benefit?.type || ""),
            name: String(benefit?.name || ""),
            description: String(benefit?.description || ""),
        }))
        .filter(
            (benefit) => filled(benefit.type) || filled(benefit.name) || filled(benefit.description)
        );
}

export function sanitizePerksPayload(payload) {
    return {
        benefits: sanitizeBenefits(payload?.benefits),
    };
}

export function filled(value) {
    return String(value || "").trim() !== "";
}

export function isValidHttpUrl(value) {
    if (!filled(value)) {
        return true;
    }

    const raw = String(value).trim();

    if (/\s/.test(raw)) {
        return false;
    }

    const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;

    try {
        const url = new URL(candidate);

        if (url.protocol !== "http:" && url.protocol !== "https:") {
            return false;
        }

        const labels = url.hostname.split(".");

        // domain.tld or www.domain.tld — at least two labels
        if (labels.length < 2) {
            return false;
        }

        const labelOk = (label) =>
            label.length > 0 &&
            label.length <= 63 &&
            /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label);

        if (!labels.every(labelOk)) {
            return false;
        }

        // Real TLD only (rs, com, si, …). Rejects www.tehnomanija / ww.nekisajt.
        const tld = labels[labels.length - 1];

        return /^[a-z]{2,6}$/i.test(tld);
    } catch {
        return false;
    }
}

export function isValidEmail(value) {
    if (!filled(value)) {
        return true;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
}

/** Digits only; optional leading +. Empty is valid (required checked separately). */
export function isValidPhone(value) {
    if (!filled(value)) {
        return true;
    }

    return /^\+?[0-9]+$/.test(String(value).trim());
}

/**
 * Live input mask for BM phone fields: optional leading +, digits only.
 * Drops spaces, letters, commas and other junk while typing.
 */
export function normalizePhoneInput(value) {
    let raw = String(value ?? "");

    if (raw === "") {
        return "";
    }

    raw = raw.replace(/[^\d+]/g, "");

    if (raw === "") {
        return "";
    }

    const hasPlus = raw.startsWith("+");
    const digits = raw.replace(/\+/g, "");

    if (digits === "") {
        return hasPlus ? "+" : "";
    }

    return hasPlus ? `+${digits}` : digits;
}

/** True when the field holds more than one phone (legacy backfill — leave alone). */
export function looksLikeMultiplePhones(value) {
    const raw = String(value ?? "").trim();

    if (raw === "") {
        return false;
    }

    if (/[,;]/.test(raw)) {
        return true;
    }

    return /\s+ili\s+/i.test(raw);
}

/**
 * Persist shape: optional leading +, digits only. Empty / junk → "".
 * Multi-number legacy values are returned unchanged (backfill / read path).
 */
export function sanitizePhone(value) {
    const raw = String(value ?? "").trim();

    if (raw === "") {
        return "";
    }

    if (looksLikeMultiplePhones(raw)) {
        return raw;
    }

    return normalizePhoneInput(raw);
}

function capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function companyStoreDetailsMissing(form) {
    return filled(form?.description) ? [] : ["description"];
}

export function keyContactsMissing(form) {
    const missing = [];

    STORE_INFO_CONTACT_ROLES.forEach((role) => {
        const contact = form?.[role.payloadKey] || {};

        if (!role.required) {
            if (filled(contact.phone) && !isValidPhone(contact.phone)) {
                missing.push(`${role.payloadKey}PhoneInvalid`);
            }
            if (filled(contact.email) && !isValidEmail(contact.email)) {
                missing.push(`${role.payloadKey}Email`);
            }
            return;
        }

        STORE_INFO_CONTACT_FIELDS.forEach((field) => {
            if (field === "email") {
                if (!filled(contact.email) || !isValidEmail(contact.email)) {
                    missing.push(`${role.payloadKey}Email`);
                }
                return;
            }

            if (field === "phone") {
                if (!filled(contact.phone) || !isValidPhone(contact.phone)) {
                    missing.push(`${role.payloadKey}Phone`);
                }
                return;
            }

            if (!filled(contact[field])) {
                missing.push(`${role.payloadKey}${capitalize(field)}`);
            }
        });
    });

    return missing;
}

export function customerSupportMissing(form) {
    const missing = [];

    if (!filled(form?.supportEmail)) {
        missing.push("supportEmail");
    } else if (!isValidEmail(form.supportEmail)) {
        missing.push("supportEmailInvalid");
    }

    if (filled(form?.supportPhone) && !isValidPhone(form.supportPhone)) {
        missing.push("supportPhoneInvalid");
    }

    if (filled(form?.faqUrl) && !isValidHttpUrl(form.faqUrl)) {
        missing.push("faqUrlInvalid");
    }

    if (!filled(form?.generalTermsUrl)) {
        missing.push("generalTermsUrl");
    } else if (!isValidHttpUrl(form.generalTermsUrl)) {
        missing.push("generalTermsUrlInvalid");
    }

    if (!filled(form?.returnPolicy)) {
        missing.push("returnPolicy");
    }

    return missing;
}

export function perksLoyaltyBenefitsMissing() {
    return [];
}

export function formatMissingWarning(t, missingKeys) {
    if (!missingKeys.length) {
        return "";
    }

    const labels = missingKeys.map((key) => t(`pages.sellerProfile.storeInfo.warnings.${key}`));
    const and = t("pages.sellerProfile.storeInfo.warnings.and");
    let list = labels[0];

    if (labels.length === 2) {
        list = `${labels[0]} ${and} ${labels[1]}`;
    } else if (labels.length > 2) {
        list = `${labels.slice(0, -1).join(", ")} ${and} ${labels[labels.length - 1]}`;
    }

    return t("pages.sellerProfile.storeInfo.warnings.stillNeeded", { list });
}

export function companyStoreDetailsComplete(payload) {
    return companyStoreDetailsMissing(payload).length === 0;
}

export function keyContactsComplete(payload) {
    return keyContactsMissing(payload).length === 0;
}

export function customerSupportComplete(payload) {
    return customerSupportMissing(payload).length === 0;
}

export function perksLoyaltyBenefitsComplete() {
    return true;
}

export const STORE_INFO_EMPTY = {
    "company-store-details": emptyCompanyStoreDetails,
    "key-contacts": emptyKeyContacts,
    "customer-support": emptyCustomerSupport,
    "perks-loyalty-benefits": emptyPerksLoyaltyBenefits,
};

export const STORE_INFO_COMPLETE = {
    "company-store-details": companyStoreDetailsComplete,
    "key-contacts": keyContactsComplete,
    "customer-support": customerSupportComplete,
    "perks-loyalty-benefits": perksLoyaltyBenefitsComplete,
};

export const STORE_INFO_MISSING = {
    "company-store-details": companyStoreDetailsMissing,
    "key-contacts": keyContactsMissing,
    "customer-support": customerSupportMissing,
    "perks-loyalty-benefits": perksLoyaltyBenefitsMissing,
};

export function isStoreInfoSectionComplete(sectionId, payload) {
    const check = STORE_INFO_COMPLETE[sectionId];
    return check ? check(payload) : false;
}

export function storeInfoSectionMissing(sectionId, payload) {
    const check = STORE_INFO_MISSING[sectionId];
    return check ? check(payload) : [];
}

export function emptyStoreInfo() {
    return Object.fromEntries(
        Object.entries(STORE_INFO_EMPTY).map(([sectionId, factory]) => [
            sectionId,
            {
                payload: factory(),
                isComplete: isStoreInfoSectionComplete(sectionId, factory()),
            },
        ])
    );
}
